import { computed, effect, inject, Injectable, Injector, Signal, signal } from '@angular/core';

import { useI18n } from '../../i18n/services/i18n.service';

export interface Pagination<T extends string> {
  /** The name of the current page */
  readonly currentName: Signal<T>;

  /** Go to a page by name, returning false if not found */
  goTo(stepName: T): boolean;

  /** Go to the next page. If there are no more, the onClose handler will be called */
  next(): void;

  /** Go to the previous page. If on the first page, no action will be performed */
  back(): void;

  /** Advance the page, skipping the given number of pages. If there are no more, the onClose handler will be called */
  skip(skip: number): void;
}

/**
 * A provider for pagination, keeping a track of the current page and the total number of pages.
 *
 * This can be provided in the outermost component.
 * The component responsible for displaying the pagination information can then inject {@link PaginationProvider},
 * and the component responsible for controlling the pagination can call {@link usePagination}.
 */
@Injectable()
export class PaginationProvider {
  readonly i18n = useI18n();

  readonly #currentPage = signal(0);
  readonly #pageCount = signal(0);

  readonly currentPage = this.#currentPage.asReadonly();
  readonly pageCount = this.#pageCount.asReadonly();

  readonly stepString = computed(() =>
    this.pageCount()
      ? this.i18n('stepNumber', { current: this.currentPage(), total: this.pageCount() })
      : this.i18n('stepNumberUnlimited', { current: this.currentPage() }),
  );

  registerPages<T extends string>(
    pages: readonly T[],
    onClose: () => void,
    options?: { injector?: Injector },
  ): Pagination<T> {
    const injector = options?.injector ?? inject(Injector);

    const currentPage = signal(1);
    effect(
      () => {
        this.#currentPage.set(currentPage());
        this.#pageCount.set(pages.length);
      },
      { injector },
    );

    const switchPage = (d: number) => {
      const newPage = currentPage() + d;
      if (newPage > pages.length) {
        onClose();
      } else {
        // The current page cannot be less than 1. If that happens, we go to the first page.
        currentPage.set(Math.max(1, newPage));
      }
    };

    return {
      currentName: computed(() => pages[currentPage() - 1]),
      goTo(pageName: T) {
        const page = pages.indexOf(pageName) + 1;
        if (page) currentPage.set(page);
        return !!page;
      },
      next() {
        switchPage(+1);
      },
      back() {
        switchPage(-1);
      },
      skip(skip: number) {
        switchPage(1 + skip);
      },
    };
  }
}

/** Register the list of pages for the current pagination context */
export const usePagination = <T extends string>(
  pages: readonly T[],
  onClose: () => void,
  options?: { injector?: Injector },
): Pagination<T> =>
  (options?.injector ?? inject(Injector)).get(PaginationProvider).registerPages(pages, onClose, options);
